import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5"
}
const _hoisted_2 = ["onSubmit"]
const _hoisted_3 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" }
const _hoisted_4 = { class: "flex justify-end gap-3 pt-2" }

import { reactive, ref, watch } from 'vue'
import { ValidationError } from 'yup'
import {
  mdiAccountOutline,
  mdiEmailOutline,
  mdiCardAccountDetailsOutline,
  mdiCakeVariantOutline,
  mdiPhoneOutline,
  mdiCalendarCheckOutline
} from '@mdi/js'

import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import type { UserDataResponse } from '@/Network/Types/Responses/UserResponse'

import SettingsCard from '../atoms/SettingsCard.vue'
import ReadonlyField from '../atoms/ReadonlyField.vue'
import { personalDataSchema } from '../../schemas'
import {
  formatCPF,
  maskCPF,
  formatPhone,
  isoToBrDate,
  brDateToIso,
  onlyDigits
} from '../../helpers/profile'

type PersonalForm = {
  name: string
  email: string
  cpf: string
  birthDate: string
  phoneNumber: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PersonalDataPanel',
  props: {
    userData: {},
    save: { type: Function }
  },
  setup(__props: any) {

const props = __props;



const isEditing = ref(false)
const saving = ref(false)
const errors = reactive<Record<string, string>>({})

const form = reactive<PersonalForm>({
  name: '',
  email: '',
  cpf: '',
  birthDate: '',
  phoneNumber: ''
})

const clearErrors = () => Object.keys(errors).forEach((key) => delete errors[key])

const populateForm = () => {
  const data = props.userData
  form.name = data?.name ?? ''
  form.email = data?.email ?? ''
  form.cpf = formatCPF(data?.cpf)
  form.birthDate = isoToBrDate(data?.birthDate)
  form.phoneNumber = formatPhone(data?.phoneNumber)
}

// Mantém o modo visualização em sincronia quando os dados chegam do backend
// (evita sobrescrever o que o usuário está digitando durante a edição).
watch(
  () => props.userData,
  () => {
    if (!isEditing.value) populateForm()
  },
  { immediate: true }
)

const startEditing = () => {
  populateForm()
  clearErrors()
  isEditing.value = true
}

const cancelEditing = () => {
  populateForm()
  clearErrors()
  isEditing.value = false
}

const handleSubmit = async () => {
  clearErrors()
  try {
    await personalDataSchema.validate(form, { abortEarly: false })
  } catch (err) {
    if (err instanceof ValidationError) {
      const inner = err.inner?.length ? err.inner : [err]
      inner.forEach((e) => {
        if (e.path) errors[e.path] = e.message
      })
    }
    return
  }

  // backend rejeita phoneNumber vazio (400) — omitir o campo quando não preenchido
  const phoneDigits = onlyDigits(form.phoneNumber)
  const payload: Partial<UserDataResponse> = {
    name: form.name.trim(),
    email: form.email.trim(),
    cpf: onlyDigits(form.cpf) || null,
    birthDate: brDateToIso(form.birthDate),
    ...(phoneDigits ? { phoneNumber: phoneDigits } : {})
  }

  saving.value = true
  try {
    await props.save(payload)
    isEditing.value = false // fecha só em sucesso real deste formulário
  } catch {
    // o pai já exibiu o toast de erro; mantém o modo de edição para não perder o que foi digitado
  } finally {
    saving.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SettingsCard, {
    title: "Dados pessoais",
    description: "Suas informações de identificação e contato."
  }, {
    action: _withCtx(() => [
      (!isEditing.value)
        ? (_openBlock(), _createBlock(OutlineButton, {
            key: 0,
            text: "Editar",
            width: "auto",
            "data-testid": "personal-data-edit-button",
            onClick: startEditing
          }))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      (!isEditing.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(ReadonlyField, {
              icon: _unref(mdiAccountOutline),
              label: "Nome",
              value: _ctx.userData?.name
            }, null, 8, ["icon", "value"]),
            _createVNode(ReadonlyField, {
              icon: _unref(mdiEmailOutline),
              label: "E-mail",
              value: _ctx.userData?.email
            }, null, 8, ["icon", "value"]),
            _createVNode(ReadonlyField, {
              icon: _unref(mdiCardAccountDetailsOutline),
              label: "CPF",
              value: _unref(maskCPF)(_ctx.userData?.cpf)
            }, null, 8, ["icon", "value"]),
            _createVNode(ReadonlyField, {
              icon: _unref(mdiCakeVariantOutline),
              label: "Data de nascimento",
              value: _unref(isoToBrDate)(_ctx.userData?.birthDate)
            }, null, 8, ["icon", "value"]),
            _createVNode(ReadonlyField, {
              icon: _unref(mdiPhoneOutline),
              label: "Telefone",
              value: _unref(formatPhone)(_ctx.userData?.phoneNumber)
            }, null, 8, ["icon", "value"]),
            _createVNode(ReadonlyField, {
              icon: _unref(mdiCalendarCheckOutline),
              label: "Conta criada em",
              value: _unref(isoToBrDate)(_ctx.userData?.createdAt)
            }, null, 8, ["icon", "value"])
          ]))
        : (_openBlock(), _createElementBlock("form", {
            key: 1,
            class: "flex flex-col gap-4",
            onSubmit: _withModifiers(handleSubmit, ["prevent"])
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(GenericInput, {
                modelValue: form.name,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.name) = $event)),
                label: "Nome",
                required: "",
                "data-testid": "personal-data-name-input",
                "error-messages": errors.name
              }, null, 8, ["modelValue", "error-messages"]),
              _createVNode(GenericInput, {
                modelValue: form.email,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.email) = $event)),
                label: "E-mail",
                type: "email",
                required: "",
                "data-testid": "personal-data-email-input",
                "error-messages": errors.email
              }, null, 8, ["modelValue", "error-messages"]),
              _createVNode(GenericInput, {
                modelValue: form.cpf,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.cpf) = $event)),
                label: "CPF",
                mask: "###.###.###-##",
                "data-testid": "personal-data-cpf-input",
                "error-messages": errors.cpf
              }, null, 8, ["modelValue", "error-messages"]),
              _createVNode(GenericInput, {
                modelValue: form.birthDate,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.birthDate) = $event)),
                label: "Data de nascimento",
                mask: "##/##/####",
                "data-testid": "personal-data-birthdate-input",
                "error-messages": errors.birthDate
              }, null, 8, ["modelValue", "error-messages"]),
              _createVNode(GenericInput, {
                modelValue: form.phoneNumber,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.phoneNumber) = $event)),
                label: "Telefone",
                mask: "(##) #####-####",
                "data-testid": "personal-data-phone-input",
                "error-messages": errors.phoneNumber
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(OutlineButton, {
                text: "Cancelar",
                width: "140px",
                type: "button",
                "data-testid": "personal-data-cancel-button",
                onClick: cancelEditing
              }),
              _createVNode(FilledButton, {
                text: "Salvar alterações",
                width: "180px",
                type: "submit",
                loading: saving.value,
                "data-testid": "personal-data-save-button"
              }, null, 8, ["loading"])
            ])
          ], 40, _hoisted_2))
    ]),
    _: 1
  }))
}
}

})